import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleSpin, handleGetSpinParams, handleBuyFeature } from "../../service";
import { initializeGame } from "./gameSettingsSlice";

// Thunk for buying bonus feature
export const rgsBuyFeature = createAsyncThunk(
  "spin/rgsBuyFeature",
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { bet } = state.game;

    try {
      const result = await handleBuyFeature(bet);
      console.log("Buy Feature Response:", result);
      return result;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to buy feature");
    }
  },
);

// Thunk for spinning the reels
export const rgsPlay = createAsyncThunk(
  "spin/rgsPlay",
  async (options = {}, { getState, rejectWithValue }) => {
    const state = getState();
    const { bet, token, featureState } = state.game;
    const { purchasedFeature, bonusCost } = options;

    try {
      // Get command and spinOptions from RGS plugin based on universal state
      const { command, spinOptions } = handleGetSpinParams({
        bet,
        featureState,
        purchasedFeature,
      });

      // Response is already transformed by RGS class
      const result = await handleSpin(
        bet,
        token,
        command,
        purchasedFeature,
        spinOptions,
      );

      // Return result with command and bonusCost included (already normalized by RGS)
      return { ...result, command, bonusCost };
    } catch (error) {
      // If error is an object with errors array (API error), return full error object
      // Otherwise return error message
      if (
        error &&
        typeof error === "object" &&
        error.errors &&
        Array.isArray(error.errors)
      ) {
        return rejectWithValue(error);
      } else {
        return rejectWithValue(error.message || "Failed to spin the reels");
      }
    }
  },
);

// Initial state for the spin slice
const initialState = {
  isSpinning: false, // Indicates if a spin is currently in progress
  status: "idle", // Tracks the status of the API request
  error: null, // Stores any error messages
  manualStop: false, // Flag to track if the spin was manually stopped
  spinComplete: true, // Indicates if the spin has completed
  windupComplete: false, // Indicates if all reels have completed their windup phase
  windupCompletedCount: 0, // Tracks how many reels have completed windup (must reach REEL_COUNT)
  reelsFrozen: false, // Flag to track if reels were frozen due to error (need recovery)
  // Feature game states (generic for any feature with parallel spin cycle)
  featureSpinActive: false, // Indicates if feature spin is in progress
  featureSpinStatus: "idle", // Tracks the status of feature spin API request
  featureSpinComplete: true, // Indicates if feature spin has completed
  featureSpinManualStop: false, // Flag to track if feature spin was manually stopped
};

// Create a slice for spin-related state and actions
const spinSlice = createSlice({
  name: "spin",
  initialState,
  reducers: {
    // Action to manually stop a spin
    manualSpinStop: (state) => {
      if (state.isSpinning && !state.manualStop) {
        state.manualStop = true; // Always mark as manual stop when requested
      }
    },

    // Action to manually set spinning state
    manualSpinStart: (state) => {
      state.isSpinning = true; // Set isSpinning
      state.spinComplete = false; // Set spinComplete
    },

    // Action to set the spin completion status
    setSpinComplete: (state, action) => {
      // Allow setting spinComplete for both succeeded and failed status
      // Failed status occurs with error 205 (free rounds) where reels still complete their animation
      if (state.status === "succeeded" || state.status === "failed") {
        state.isSpinning = false; // Mark spinning as false
        state.spinComplete = action.payload; // Update spin completion status
      }
    },

    // Action to mark reels as frozen (need recovery)
    setReelsFrozen: (state, action) => {
      state.reelsFrozen = action.payload;
    },

    // Action to set the windup completion status
    setWindupComplete: (state, action) => {
      state.windupComplete = action.payload; // Update windup completion status
    },

    // CRITICAL: Increment windup counter - called by each reel when it completes windup
    // Only sets windupComplete to true when ALL reels (5) have completed
    incrementWindupCount: (state) => {
      state.windupCompletedCount += 1;

      // Only set windupComplete when ALL 5 reels have finished windup
      if (state.windupCompletedCount === 5) {
        state.windupComplete = true;
      }
    },

    // Reset windup counter - called when a new spin starts
    resetWindupCount: (state) => {
      state.windupCompletedCount = 0;
      state.windupComplete = false;
    },

    // Feature game actions (generic for any feature with parallel spin cycle)
    // Action to set feature spin active state
    setFeatureSpinActive: (state, action) => {
      state.featureSpinActive = action.payload;
    },

    // Action to manually stop a feature spin
    featureSpinManualStop: (state) => {
      if (state.featureSpinActive && !state.featureSpinManualStop) {
        state.featureSpinManualStop = true;
      }
    },

    // Action to set feature spin completion status
    setFeatureSpinComplete: (state, action) => {
      if (state.featureSpinStatus === "succeeded") {
        state.featureSpinActive = false;
        state.featureSpinComplete = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(rgsPlay.pending, (state) => {
        // Check if feature spin was already active - if so, this is a feature spin
        // Otherwise, assume it's a regular spin
        const isFeatureSpin = state.featureSpinActive;

        if (isFeatureSpin) {
          // Update feature spin states
          state.featureSpinStatus = "pending";
          state.featureSpinComplete = false;
          state.featureSpinManualStop = false;
        } else {
          // Update regular game states
          state.error = null;
          state.status = "pending";
          state.isSpinning = true;
          state.spinComplete = false;
          state.windupComplete = false;
          state.windupCompletedCount = 0; // Reset counter for new spin
          state.manualStop = false;
        }
      })
      .addCase(rgsPlay.fulfilled, (state, action) => {
        // Use command only: respin = feature spin (base reels not used), spin = base game logic (includes free rounds)
        const isRespin = action.payload.command === "respin";
        const wasFeatureSpin = state.featureSpinActive;

        if (isRespin) {
          state.featureSpinStatus = "succeeded";
          state.featureSpinActive = true;
          state.featureSpinComplete = false;
          state.featureSpinManualStop = false;

          if (wasFeatureSpin) {
            state.isSpinning = false;
            state.status = "idle";
            state.spinComplete = true;
          } else {
            state.status = "succeeded";
            // Do NOT set isSpinning or spinComplete - reel flow handles them
          }
        } else {
          // Spin command: base game or free rounds - same isSpinning logic
          state.status = "succeeded";
          state.featureSpinActive = false;
          state.featureSpinStatus = "idle";
          state.featureSpinComplete = true;
          // Do NOT set isSpinning or spinComplete - reel flow handles them
        }
      })
      .addCase(rgsPlay.rejected, (state, action) => {
        // Determine which spin type failed based on current state
        const isFeatureSpin = state.featureSpinActive;

        if (isFeatureSpin) {
          // Update feature spin states
          state.featureSpinStatus = "failed";
          state.featureSpinActive = false;
        } else {
          // Update regular game states
          state.status = "failed";
          state.isSpinning = false;
          state.error = action.payload || "Spin failed";
          // spinComplete will be set by ReelController when reels finish stopping
        }
      })
      // Handle feature completion reload from initializeGame
      .addCase(initializeGame.fulfilled, (state, action) => {
        const { result } = action.payload;
        const { reloadState } = result;

        // Check if we have any feature completion reload from RGS
        if (reloadState?.needsReload && reloadState.phase === "completion") {
          // Set feature spin state to completed (allows dialog trigger effect to fire)
          state.featureSpinStatus = "succeeded";
          state.featureSpinActive = false;
          state.featureSpinComplete = true;
          state.featureSpinManualStop = false;
        }
      });
  },
});

// ==============================
// 🔹 Selectors
// ==============================

export const selectIsSpinning = (state) => state.spin.isSpinning;
export const selectSpinStatus = (state) => state.spin.status;
export const selectSpinError = (state) => state.spin.error;
export const selectManualStop = (state) => state.spin.manualStop;
export const selectSpinComplete = (state) => state.spin.spinComplete;
export const selectWindupComplete = (state) => state.spin.windupComplete;
export const selectReelsFrozen = (state) => state.spin.reelsFrozen;
export const selectFeatureSpinActive = (state) =>
  state.spin.featureSpinActive;
export const selectFeatureSpinStatus = (state) => state.spin.featureSpinStatus;
export const selectFeatureSpinComplete = (state) =>
  state.spin.featureSpinComplete;
export const selectFeatureSpinManualStop = (state) =>
  state.spin.featureSpinManualStop;

// ==============================
// 🔹 Export Actions
// ==============================

export const {
  manualSpinStop,
  manualSpinStart,
  setSpinComplete,
  setWindupComplete,
  incrementWindupCount,
  resetWindupCount,
  setReelsFrozen,
  setFeatureSpinActive,
  featureSpinManualStop,
  setFeatureSpinComplete,
} = spinSlice.actions;

// Export the reducer to be included in the store
export default spinSlice.reducer;
